import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { TextOverflowTooltip } from '@ipfabric/ui-components/Tooltip/TextOverflowTooltip';
import { maybeWrapWithTooltip } from '@ipfabric/ui-components/Tooltip/helpers/maybeWrapWithTooltip';
import { pick } from '@ipfabric/utils';
import classNames from 'classnames';
import {
  type SelectOption,
  type SelectOptionType,
  isNestedOption,
} from 'components/Select/SelectOption';
import { filterSelectOptions } from 'components/Select/helpers/filterSelectOptions';
import {
  type SelectSearchablePassthruProps,
  useSelectSearchable,
} from 'components/Select/useSelectSearchable';
import { useEffect, useMemo, useRef } from 'react';
import type { FocusEvent, ReactNode, SyntheticEvent } from 'react';
import './Select.css';
import { horizontalEllipsis } from '@ipfabric/frontend-shared-assets/utils/characters';
import { useClickAway } from '@ipfabric/ui-components/hooks/useClickAway';
import { isAnyEnter } from '@ipfabric/ui-components/utils/keyboard/keyboardKeys';
import { FormInputButton } from 'components/FormInputButton.tsx/FormInputButton';
import type { JSX } from 'react';
import { getSizeCssClass } from 'utils/form/getSizeCssClass';
import {
  type ArrowKeyNavigationMouseEnter,
  arrowKeyNavItemClass,
} from 'utils/keyboard/useArrowKeyNavigation';
import { SelectMenu } from './SelectMenu';

/**
 * Function to get comparable string from value
 */
export type GetSelectOptionValueFunction<TValue> = (value: TValue) => string;

export type SelectProps<TValue> = {
  className?: string;
  options: SelectOption<TValue>[];
  placeholder?: string;
  hasError?: boolean;
  size?: 'normal' | 'small' | 'tiny';
  /**
   * Expand the select to fill the whole width of the parent container when the dropdown is open.
   *
   * @deprecated This has only one usage, and it is troublesome to maintain.
   * Using a design that does not need ad-hoc fiddling with component dimensions is recommended instead.
   */
  stretchOnOpenDropdown?: boolean;
  loading?: boolean;
  formatValueLabel?: (label: string) => string;
  onScrollBottom?: () => void;
  autoFocus?: boolean;
  dataTestId?: string;
} & SelectSearchablePassthruProps &
  (SelectOptionType<TValue> extends { value: string | null }
    ? // this part is a bit Typescript bleeding edge:
      //   - we can't check just TValue extends string | null above, it raises type errors and I have no idea why
      //   - I tried several approaches to not include the null for nonClearable select but with no luck.
      { getOptionValue?: GetSelectOptionValueFunction<TValue | null> }
    : { getOptionValue: GetSelectOptionValueFunction<TValue | null> }) &
  (
    | {
        nonClearable?: boolean;
        value: TValue | null;
        onChange: (value: TValue | null) => void;
      }
    | {
        nonClearable: true;
        value: TValue;
        onChange: (value: TValue) => void;
      }
  );

export const getSelectOptionValueDefault = (
  value: string | null,
  // using uuid for null value in case null was an actual
  // option value. This can happen if there is need for
  // explicitly selectable empty value
): string => value ?? 'ea360eb9-7d44-4995-b51e-70f39aff375d';

/**
 * Get string representation of Select object value determined by given key properties
 *
 * Although JSON.stringify is known to be slow on large objects, but
 * I just checked on a simple benchmark that running it for tiny objects
 * has performance impact close to zero.
 */

export const makeGetSelectOptionValueFromObject =
  <T,>(...keyProps: readonly (keyof T)[]) =>
  (value: T | null): string =>
    JSON.stringify(value === null ? value : pick<keyof T>(keyProps)(value));

const selectMsg = {
  placeholder: `Select${horizontalEllipsis}`,
  noOptions: 'No options',
  loading: 'Loading options',
};

export const Select = <TValue,>({
  getOptionValue = getSelectOptionValueDefault as GetSelectOptionValueFunction<TValue | null>,
  formatValueLabel = (label) => label,
  ...props
}: SelectProps<TValue>): JSX.Element => {
  const crossButtonRef = useRef<HTMLButtonElement>(null);
  const focusContainerRef = useRef<HTMLDivElement | null>(null);
  const {
    blur,
    focused,
    menuOpen,
    handleChevronClick,
    handleControlClick,
    handleControlFocus,
    handleSearchChange,
    handleSearchKeyDown,
    handleControlKeyDown,
    searchInputRef,
    searchValue,
    setFocused,
    setMenuOpen,
    setSearchValue,
  } = useSelectSearchable({
    ...props,
    focusContainerRef,
    throttle: 50,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: need to run this effect only on mount
  useEffect(() => {
    if (props.autoFocus) {
      setFocused(true);
    }
  }, []);

  const handleSelect = (option: SelectOptionType<TValue>) => {
    props.onChange(option.value);
    setSearchValue(''); // clear search input to display the selected value in its placeholder
    setMenuOpen(false);

    (searchInputRef.current ?? focusContainerRef.current)?.focus();
  };

  const handleClearClick = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    props.onChange(null!); // handled in props type that clearable input must support null

    blur();
  };

  const filteredOptions = useMemo(
    () =>
      props.searchable && searchValue
        ? filterSelectOptions(props.options, searchValue)
        : props.options,
    [searchValue, props.searchable, props.options],
  );

  const renderOption = (
    option: SelectOptionType<TValue>,
    handleOptionMouseEnter: ArrowKeyNavigationMouseEnter,
    nested?: boolean,
  ) =>
    maybeWrapWithTooltip(
      option.tooltip
        ? {
            content: option.tooltip,
            placement: 'right',
          }
        : null,
      getOptionValue(option.value),
    )(
      <div
        key={getOptionValue(option.value)}
        className={classNames(
          'select__option select__option-label',
          arrowKeyNavItemClass,
          option.disabled && 'select__option--disabled',
          nested && 'select__option--nested',
          getOptionValue(props.value) === getOptionValue(option.value) &&
            'select__option--selected',
        )}
        data-testid={`select-option-${option.label}`}
        onClick={() => {
          if (!option.disabled) {
            handleSelect(option);
          }
        }}
        onKeyUp={(e) => {
          if ((isAnyEnter(e.key) || e.key === ' ') && !option.disabled) {
            e.preventDefault();
            handleSelect(option);
          }
        }}
        onMouseEnter={handleOptionMouseEnter}
        tabIndex={-1}
      >
        <TextOverflowTooltip>
          {option.content ?? option.label}
        </TextOverflowTooltip>
      </div>,
    );

  // biome-ignore lint/correctness/useExhaustiveDependencies(formatValueLabel): expected stable fn
  // biome-ignore lint/correctness/useExhaustiveDependencies(getOptionValue): expected stable fn
  const displayLabel = useMemo(() => {
    let foundLabel: ReactNode = props.placeholder ?? selectMsg.placeholder;
    props.options.some((option) => {
      if (isNestedOption(option)) {
        return option.options.some(({ label, value, content }) => {
          if (getOptionValue(value) === getOptionValue(props.value)) {
            foundLabel = content ?? formatValueLabel(label);
            return true;
          }
          return false;
        });
      }
      if (getOptionValue(option.value) === getOptionValue(props.value)) {
        foundLabel = option.content ?? formatValueLabel(option.label);
        return true;
      }
      return false;
    });

    return foundLabel;
  }, [props.options, props.value, props.placeholder]);

  const wrapperRef = useClickAway<HTMLDivElement>(blur);

  const hasSearchInput = !!(props.searchable || props.onInputValueChange);

  return (
    <div
      className={classNames(
        'select',
        getSizeCssClass(props.size, 'select'),
        props.className,
        props.stretchOnOpenDropdown && menuOpen && 'absolute z-dropdown w-full',
      )}
      ref={wrapperRef}
      onKeyDown={handleControlKeyDown}
      data-testid={props.dataTestId}
    >
      <div
        className={classNames(
          'select__control',
          props.disabled && 'select__control--disabled',
          props.hasError && 'select__control--error',
          focused && 'select__control--focused',
          menuOpen && 'select__control--open',
        )}
        onClick={handleControlClick}
        onFocus={(e: FocusEvent<HTMLDivElement | HTMLButtonElement>) => {
          if (e.target !== crossButtonRef.current) {
            handleControlFocus(e);
          }
        }}
        ref={focusContainerRef}
        tabIndex={hasSearchInput ? -1 : 0}
      >
        {hasSearchInput ? (
          <input
            className='select__search-input'
            type='text'
            placeholder={
              typeof displayLabel === 'string' ? displayLabel : undefined
            }
            ref={searchInputRef}
            value={searchValue}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            disabled={props.disabled}
            onFocus={() => setFocused(true)}
          />
        ) : (
          <div className='select__label'>{displayLabel}</div>
        )}
        <div className='flex'>
          {props.value !== null && !props.nonClearable && (
            <FormInputButton
              className='mr-2'
              accent='negative'
              disabled={props.disabled}
              onClick={handleClearClick}
              buttonRef={crossButtonRef}
            >
              <Icon glyph='fa-xmark' size='xs' />
            </FormInputButton>
          )}
          {props.loading ? (
            <Icon className='fa-spin' glyph='fa-spinner' size='xs' />
          ) : (
            <FormInputButton
              disabled={props.disabled}
              onClick={handleChevronClick}
            >
              <Icon
                className='select__dropdown-indicator'
                glyph={menuOpen ? 'fa-chevron-up' : 'fa-chevron-down'}
                size='xs'
              />
            </FormInputButton>
          )}
        </div>
      </div>
      {menuOpen && (
        <SelectMenu
          onScrollBottom={props.onScrollBottom}
          wrapperRef={wrapperRef}
          render={(handleOptionMouseEnter: ArrowKeyNavigationMouseEnter) => (
            <>
              {filteredOptions.length === 0 ? (
                <div className='select__option select__option-label select__option--no-options'>
                  {props.loading ? selectMsg.loading : selectMsg.noOptions}
                </div>
              ) : (
                filteredOptions.map((option) => {
                  if (isNestedOption(option)) {
                    return (
                      <div
                        key={option.label}
                        // biome-ignore lint/a11y/useSemanticElements: This div is used as a custom select option container but is not a semantic list item
                        role='listitem'
                      >
                        <div className='px-2 py-3 font-semibold'>
                          {option.label}
                        </div>
                        {option.options.map((nestedOption) =>
                          renderOption(
                            nestedOption,
                            handleOptionMouseEnter,
                            true,
                          ),
                        )}
                      </div>
                    );
                  }
                  return renderOption(option, handleOptionMouseEnter);
                })
              )}
              {filteredOptions.length > 0 && props.loading && (
                <div className='select__option select__option-label select__option--no-options'>
                  {selectMsg.loading}
                </div>
              )}
            </>
          )}
        />
      )}
    </div>
  );
};
